import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Divider, Typography } from '@mui/material';
import { Gear } from '@phosphor-icons/react';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { FlowService } from '../../../service/FlowService';
import { useNotificationStore } from '../../../store/storeNotifications';
import { MuiInputControlled } from '../../Form/Input';

const NameAndSetupSchema = z.object({
  name: z.string(),
  description: z.string(),
  title1: z.string(),
  conversationStarter1: z.string(),
  title2: z.string(),
  conversationStarter2: z.string(),
  title3: z.string(),
  conversationStarter3: z.string(),
});

type INameAndSetup = z.infer<typeof NameAndSetupSchema>;

interface NameAndSetupProps {
  chatData: any;
  onSetFlow: any;
}

export function NameAndSetup({ chatData, onSetFlow }: NameAndSetupProps) {
  const setNotification = useNotificationStore(state => state.setNotification);
  const formsMethods = useForm<INameAndSetup>({
    resolver: zodResolver(NameAndSetupSchema),
  });

  useEffect(() => {
    if (chatData) {
      formsMethods.setValue('name', chatData.name);
      formsMethods.setValue(
        'description',
        chatData.chatDescription === null ? '' : chatData.chatDescription,
      );
      formsMethods.setValue(
        'title1',
        chatData.chatActions.length === 0 ? '' : chatData.chatActions[0].name,
      );
      formsMethods.setValue(
        'conversationStarter1',
        chatData.chatActions.length === 0
          ? ''
          : chatData.chatActions[0].description,
      );
      formsMethods.setValue(
        'title2',
        chatData.chatActions.length === 0 ? '' : chatData.chatActions[1].name,
      );
      formsMethods.setValue(
        'conversationStarter2',
        chatData.chatActions.length === 0
          ? ''
          : chatData.chatActions[1].description,
      );
      formsMethods.setValue(
        'title3',
        chatData.chatActions.length === 0 ? '' : chatData.chatActions[2].name,
      );
      formsMethods.setValue(
        'conversationStarter3',
        chatData.chatActions.length === 0
          ? ''
          : chatData.chatActions[2].description,
      );
    }
  }, [chatData]);

  async function handleSubmitForms(data: INameAndSetup) {
    try {
      const response = await FlowService.updateFlow(chatData.id, {
        name: data.name,
        chatDescription: data.description,
        chatActions: [
          { name: data.title1, description: data.conversationStarter1 },
          { name: data.title2, description: data.conversationStarter2 },
          { name: data.title3, description: data.conversationStarter3 },
        ],
      });

      onSetFlow(response.data)

      setNotification({
        type: 'success',
        message: 'Saved successfully',
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Error saving name and setup form',
      });
    }
  }

  return (
    <FormProvider {...formsMethods}>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        height="600px"
      >
        <Box display="flex" flexDirection="column" gap="16px" height="600px">
          <Box
            display="flex"
            alignItems="center"
            gap="8px"
            width="100%"
            mb="8px"
          >
            <Gear size={20} color="#101828" />
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: '500',
                color: '#101828',
              }}
            >
              Name and setup
            </Typography>
          </Box>

          <form
            id="name-and-setup"
            onSubmit={formsMethods.handleSubmit(handleSubmitForms)}
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            <MuiInputControlled
              inputProps={{
                name: 'name',
                label: 'Add name',
                placeholder: 'Type your Fluxbot name here',
              }}
              styles={{ width: '318px' }}
            />

            <MuiInputControlled
              inputProps={{
                name: 'description',
                label: 'Add description',
                placeholder: 'Type your Fluxbot description here',
              }}
              styles={{ width: '318px' }}
            />

            <Divider />

            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#101828',
                marginBottom: '8px',
              }}
            >
              Conversation starters
            </Typography>

            <MuiInputControlled
              inputProps={{
                name: 'title1',
                label: 'Title 1 (optional)',
                placeholder: 'Type your title',
              }}
              styles={{ width: '249px' }}
            />

            <MuiInputControlled
              inputProps={{
                name: 'conversationStarter1',
                label: 'Conversation starter 1 (optional)',
                placeholder:
                  'Start fresh by setting up a new project tailored to your needs. Choose your goals and get started in just a few clicks',
              }}
            />

            <MuiInputControlled
              inputProps={{
                name: 'title2',
                label: 'Title 2 (optional)',
                placeholder: 'Type your title',
              }}
              styles={{ width: '249px' }}
            />

            <MuiInputControlled
              inputProps={{
                name: 'conversationStarter2',
                label: 'Conversation starter 2 (optional)',
                placeholder: 'Type your conversation starter here',
              }}
            />

            <MuiInputControlled
              inputProps={{
                name: 'title3',
                label: 'Title 3 (optional)',
                placeholder: 'Type your title',
              }}
              styles={{ width: '249px' }}
            />

            <MuiInputControlled
              inputProps={{
                name: 'conversationStarter3',
                label: 'Conversation starter 3 (optional)',
                placeholder: 'Type your conversation starter here',
              }}
            />
          </form>
        </Box>

        <Box display="flex" justifyContent="end" width="100%">
          <Button
            form="name-and-setup"
            type="submit"
            variant="contained"
            sx={{ width: '160px' }}
          >
            Save changes
          </Button>
        </Box>
      </Box>
    </FormProvider>
  );
}
